import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { booleanBadgeOptions } from 'src/utils/constants';
import { commonMessages, generalMessages } from 'src/utils/messages';
import { voucherTypesMessages } from 'src/features/voucherTypes/utils/messages';
import { BillableMetric } from 'src/features/billableMetrics/utils/types';
import { useListBillableMetrics } from 'src/features/billableMetrics/api/useListBillableMetrics';
import { billableMetricsMessages } from 'src/features/billableMetrics/utils/messages';
import { AGGREGATION_TYPE_MAP, MEASUREMENT_TYPE_BADGE_OPTIONS } from 'src/features/billableMetrics/utils/constants';
import CreateBillableMetricFormModal from 'src/features/billableMetrics/components/forms/CreateBillableMetricFormModal';
import CellBillableMetricActions from 'src/features/billableMetrics/components/tables/CellBillableMetricActions';

const BillableMetricsTable = () => {
    const { formatMessage } = useIntl();

    const permissionManageServicePlans = useHasPermissions([PERMISSION.MANAGE_SERVICE_PLANS]);

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'name', desc: false }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: billableMetrics,
        isError,
        isLoading,
    } = useListBillableMetrics({
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<BillableMetric>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: ({ row }) => (
                <div>
                    <div className="text-sm font-medium text-gray-900">{row.original.name}</div>
                    <div className="text-sm text-gray-600">{row.original.code}</div>
                </div>
            ),
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('measurement_type', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(billableMetricsMessages.measurementType),
                badgeOptions: MEASUREMENT_TYPE_BADGE_OPTIONS,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
        }),
        columnHelper.accessor('aggregation_type', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => formatMessage(AGGREGATION_TYPE_MAP[row.original.aggregation_type]),
            meta: {
                title: formatMessage(billableMetricsMessages.aggregationType),
            },
        }),
        columnHelper.accessor('description', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => row.original.description ?? '-',
            meta: {
                title: formatMessage(generalMessages.description),
            },
        }),
        columnHelper.accessor('is_active', {
            enableSorting: true,
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(voucherTypesMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        /*
         * Gated as a whole because this entity has no dashboard - there is nothing in the column for
         * a read-only user. Tables whose rows DO have a dashboard must never do this.
         */
        ...(permissionManageServicePlans
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: CellBillableMetricActions,
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<BillableMetric, unknown>
            columns={columns}
            data={billableMetrics}
            semantics={{
                title: formatMessage(billableMetricsMessages.billableMetrics),
                entryName: formatMessage(billableMetricsMessages.billableMetric).toLowerCase(),
                entryNames: formatMessage(billableMetricsMessages.billableMetrics).toLowerCase(),
                placeholder: formatMessage(billableMetricsMessages.billableMetric).toLowerCase(),
            }}
            search={search}
            filters={filters}
            sorting={sorting}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageServicePlans && { newEntry: CreateBillableMetricFormModal }),
            }}
        />
    );
};

export default BillableMetricsTable;
